import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import type { NgxForm } from '../form/ngx-form.types';
import { FrontendMandant, GlobalFrontendUser } from '@reflact/reflactportal';
import { ToastrService } from 'ngx-toastr';
import type { MenuItem } from 'primeng/api';
import type { PortalFeedbackOptions } from '@reflact/portal-ecosystem';
import { OverlayAsideComponent } from '../overlay-aside/overlay-aside.component';
import type { INavData } from '../body-layout/inav-data';
import { OverlayService } from '../../services/overlay.service';
import { LoginService } from '../../services/login.service';
import { UserService } from '../../services/user.service';
import { ThemeService } from '../../services/theme.service';
import { MandantService } from '../../services/mandant.service';
import { LogtoService } from '../../services/logto.service';
import { ActivityService } from '../../services/activity.service';

@Component({
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: false
})
export class LayoutComponent implements OnInit {
  public readonly logtoService = inject(LogtoService);
  public readonly themeService = inject(ThemeService);
  private readonly mandantService = inject(MandantService);
  private readonly activityService = inject(ActivityService);
  /** Badge am Portal-Logo, wenn der aktuelle Mandant als intern (kein Kunde) markiert ist. */
  public logoBadge = '';
  private readonly internalLabel = $localize`:@@layoutInternalBadge:Intern`;
  public navItems: INavData[] = [];
  public formFields: NgxForm.FormField[] = [
    { fieldName: "email", label: "Email", type: 'text', required: true },
    { fieldName: "name", label: "Name", type: 'text', required: true }
  ];
  public translatedLabels = new Map<string, string>([
    ["email", $localize`:@@globalEmail:Email`],
    ["name", $localize`:@@globalName:Name`]
  ])
  @ViewChild("selfUserSettings") public selfUserSettings!: OverlayAsideComponent;
  public mandants: FrontendMandant[] = []
  public currentMandant = "";
  public currentMandantName = "";
  public switchingMandant = false;
  public leavingMandant = false;
  private accountRefreshPending = false;
  public refreshingAccount = false;
  public savingSelfUser = false;
  public layoutReady = false;
  public canManageCompany = false;
  /** "Feedback geben" im Nutzermenü: Meldungen aus dem Portal selbst landen im FEEDBACK_PORTAL_REPOSITORY. */
  public readonly feedbackOptions: PortalFeedbackOptions = {
    baseUrl: new URL('/api/feedback', window.location.origin).toString(),
    getAccessToken: () => this.logtoService.getOrganizationToken(this.currentMandant)
  };

  public get showSidebar(): boolean {
    return this.isAdminRoute;
  }

  public get menuMandants() {
    return this.mandants.map(mandant => ({ id: mandant._id.toString(), name: mandant.name }));
  }

  public accountOpened(): void {
    this.accountRefreshPending = true;
  }

  @HostListener('window:focus')
  @HostListener('document:visibilitychange')
  public async refreshAccountOnReturn(): Promise<void> {
    if (!this.accountRefreshPending || document.visibilityState !== 'visible' ||
      this.refreshingAccount || this.leavingMandant || this.switchingMandant) return;
    this.accountRefreshPending = false;
    this.refreshingAccount = true;
    try {
      await this.loginService.refreshAccount();
      if (this.selfUserSettings?.selectedObject) await this.openSelfUserSettings();
    } catch {
      this.toastrService.error('Die Kontodaten konnten nicht aktualisiert werden. Bitte erneut anmelden.');
    } finally {
      this.refreshingAccount = false;
      this.cdr.detectChanges();
    }
  }

  public async leaveMandant(mandantId: string): Promise<void> {
    if (this.leavingMandant || this.switchingMandant || this.refreshingAccount || this.mandants.length < 2 ||
      !this.mandants.some(mandant => mandant._id.toString() === mandantId)) return;
    this.leavingMandant = true;
    let removed = false;
    try {
      const result = await this.userService.leaveMandant(mandantId);
      if (result.status !== 'ok') throw new Error('Der Mandant konnte nicht verlassen werden.');
      removed = true;
      this.mandants = result.mandants;
      this.buildContextItems();
      if (mandantId === this.currentMandant) {
        await this.loginService.selectAfterLeaving(this.mandants[0]._id.toString());
        window.location.assign(this.router.serializeUrl(this.router.createUrlTree(['/'])));
      } else {
        await this.loginService.refreshAccount();
      }
      this.toastrService.success('Mandant verlassen.');
    } catch (error) {
      const message = error instanceof HttpErrorResponse
        ? (typeof error.error === 'string' ? error.error : error.error?.message)
        : error instanceof Error ? error.message : undefined;
      this.toastrService.error(removed ? 'Mandant verlassen. Bitte erneut anmelden.' : message || 'Der Mandant konnte nicht verlassen werden.');
      if (removed) await this.router.navigate(['/login']);
    } finally {
      this.leavingMandant = false;
      this.cdr.detectChanges();
    }
  }
  /** Steuert, ob das Topbar-Dropdown "Superadmin" statt des zuletzt gewählten Mandantennamens
   * zeigt \u2014 wird pro Navigation zwischen den beiden Root-Routen ('' und 'admin') neu gesetzt,
   * siehe ngOnInit(). */
  public isAdminRoute = false; public readonly adminAreaLabel = $localize`:@@layoutSuperadminMenuItem:Superadmin`;
  constructor(public loginService: LoginService, public overlayService: OverlayService, public userService: UserService, public toastrService: ToastrService, public router: Router, private cdr: ChangeDetectorRef) {

  }


  /** Mandanten-/Superadmin-Wechsel-Menü; interne Mandanten tragen ein Badge (MenuItem.badge). */
  public contextItems: MenuItem[] = [];

  private buildContextItems(): void {
    this.contextItems = [
      ...this.mandants.map((mandant): MenuItem => ({
        label: mandant.name,
        icon: 'ri-building-line',
        badge: (mandant as { internal?: boolean }).internal ? this.internalLabel : undefined,
        badgeStyleClass: 'internal-menu-badge',
        command: () => this.changeMandant(mandant._id.toString())
      })),
      ...this.additionalContexts
    ];
  }

  public get additionalContexts(): MenuItem[] {
    const items: MenuItem[] = [];
    if (this.loginService.loggedInUser?.reflactadmin) {
      items.push({
        label: $localize`:@@layoutSuperadminMenuItem:Superadmin`,
        icon: 'ri-shield-user-line',
        command: () => this.router.navigate(['/admin'])
      });
    }
    return items;
  }

  async ngOnInit(): Promise<void> {
    await this.logtoService.isAuthenticated().catch(() => false);
    const res = (await this.loginService.getTokenAndLogin());
    if (res.status === 'ok') this.activityService.record('portal_visit');
    this.currentMandant = res.mandant?._id?.toString() ?? '';
    this.currentMandantName = res.mandant?.name ?? '';
    this.canManageCompany = !!res.mandant && !!res.mandantUser?.permissions.includes('admin');
    this.mandants = (await this.loginService.getMandantsForUser()).mandants;
    if (res.mandant) {
      this.logoBadge = await this.mandantService.isCurrentMandantInternal().catch(() => false) ? this.internalLabel : '';
    }
    this.buildContextItems();
    // On a hard/initial navigation to a nested route, Router.url can still lag behind the
    // actual browser URL at this point — read location.pathname instead to avoid a race
    // where navItems stays empty and app-body-layout (and its router-outlet) never renders.
    const isAdminRoute = window.location.pathname.includes("/admin");
    this.isAdminRoute = isAdminRoute;
    if (res.mandant != undefined && res.mandantUser != undefined) {
      if (!isAdminRoute) {
        this.navItems.push(
          {
            name: $localize`:@@navHome:Home`,
            url: "/home",
            icon: "ri-dashboard-line"
          },
          {
            name: $localize`:@@navToolsAndTrainings:Tools & Trainings`,
            url: "/portal",
            icon: "ri-pencil-ruler-2-line"
          }
        )
        if (res.mandantUser.permissions.indexOf("admin") > -1) {
          this.navItems.push(
            {
              name: $localize`:@@navUserAdmin:Nutzerverwaltung`,
              url: "/users",
              icon: "ri-user-line"
            }
          )
        }
      }
    }
    if (isAdminRoute) {



      if (res.globalUser?.reflactadmin) {
        this.navItems.push(
          {
            name: $localize`:@@navAdmin:Administration`,
            title: true,
            children: [
              {
                name: $localize`:@@navDashboard:Dashboard`,
                url: "/admin/dashboard",
                icon: "ri-dashboard-line"
              },
              {
                name: $localize`:@@navTileAdmin:Kachelverwaltung`,
                url: "/admin/tiles",
                icon: "ri-layout-grid-line"
              },
              {
                name: $localize`:@@navTileResponsibility:Kachelverantwortung`,
                url: "/admin/tile-responsibility",
                icon: "ri-user-follow-line"
              },
              {
                name: $localize`:@@navFeedbackAdmin:Feedback`,
                url: "/admin/feedback",
                icon: "ri-feedback-line"
              },
              {
                name: $localize`:@@navMandantAdmin:Mandantenverwaltung`,
                url: "/admin/mandants",
                icon: "ri-building-line"
              },
              {
                name: $localize`:@@navGlobalUserAdmin:Globale Nutzerverwaltung`,
                url: "/admin/users",
                icon: "ri-user-star-line"
              },
              {
                name: $localize`:@@navSettingsAdmin:Einstellungen`,
                url: "/admin/settings",
                icon: "ri-settings-3-line"
              }
            ]
          }
        )
      }
    }

    if (this.overlayService.get("selected") == "selfuser") {
      this.openSelfUserSettings();
    }
    this.layoutReady = true;
    this.cdr.detectChanges();
  }




  public async changeMandant(mandantId: string) {
    if (this.switchingMandant || this.leavingMandant || this.refreshingAccount || !this.mandants.some(mandant => mandant._id.toString() === mandantId)) return;
    this.switchingMandant = true;
    try {
      const result = await this.loginService.selectMandant(mandantId);
      if (result.status !== 'ok') throw new Error('Mandantenwechsel fehlgeschlagen.');
      window.location.assign(this.router.serializeUrl(this.router.createUrlTree(['/'])));
    } catch {
      this.toastrService.error('Mandantenwechsel fehlgeschlagen.');
    } finally {
      this.switchingMandant = false;
      this.cdr.detectChanges();
    }
  }

  public async openSelfUserSettings() {
    this.selfUserSettings.setSelected({ "_id": "selfuser", "data": this.loginService.loggedInUser });
  }

  public async updateSelfUser(data: GlobalFrontendUser) {
    this.savingSelfUser = true;
    this.cdr.detectChanges();
    let res;
    try {
      res = await this.userService.editMyGlobalUser(data.email, data.name);
    } finally {
      this.savingSelfUser = false;
      this.cdr.detectChanges();
    }
    if (res.status == "ok") {
      this.toastrService.success($localize`:@@globalSaved:Gespeichert!`);
    }
  }

  public async logoutFn() {
    localStorage.removeItem("authToken");
    try {
      // Beendet auch die Logto-SSO-Session; die URL muss in Logto als Post-Sign-out-Redirect-URI eingetragen sein.
      await this.logtoService.signOut(new URL('login', document.baseURI).toString());
    } catch {
      window.location.href = '/';
    }
  }

}
